import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { AppConfig } from 'src/app/core/_config/app.config';
import { CustomResponse, CustomResponseData } from 'src/app/interfaces/CustomResponse';
import { DownloadedFile } from '../../file-remitance/models/downloadFile';
import { catchError, map } from 'rxjs/operators';
import { FilePaginatorRem } from '../models/automaticDebitRemFile';
import { DirectDebitContractFile } from '../models/directDebitContractLite';
import { AutomaticDebitRetFile, FilePaginatorRet } from '../models/automaticDebitRetFile';

@Injectable({
    providedIn: 'root',
})
export class AutomaticDebitService {
    private API_URL = this.config.urlApi + this.config.versionApi;
    constructor(private http: HttpClient, private config: AppConfig) {}

    GetRetFile(id: string): Observable<CustomResponseData<AutomaticDebitRetFile>> {
        return this.http.get<CustomResponseData<AutomaticDebitRetFile>>(
            `${this.API_URL}AutomaticDebit/GetRetFile/${id}`,
        );
    }
    GetBilletData(billetId: string): Observable<any> {
        return this.http.get<CustomResponseData<any>>(`${this.API_URL}AutomaticDebit/GetBilletData/${billetId}`);
    }

    ReceivingOtherBillets(payload: {
        FileRetId: string;
        LineId: string;
        Username: string;
        SelectedBillets: { BilletNumber: string; BilletIdExternal: string }[];
    }): Observable<any> {
        return this.http.post<any>(`${this.API_URL}AutomaticDebit/ReceivingOtherBillets`, payload);
    }

    deleteRetId(fileRetId: string): Observable<any> {
        return this.http.delete<any>(`${this.API_URL}AutomaticDebit/Ret/${fileRetId}`);
    }

    deleteRemId(fileRemId: string): Observable<any> {
        return this.http.delete<any>(`${this.API_URL}AutomaticDebit/Rem/${fileRemId}`);
    }

    GetAuthorizedByDate(dateStr: string, legalEntityId: string, bankCode: number): Observable<any> {
        return this.http.get<CustomResponse<any>>(
            `${this.API_URL}AutomaticDebit/GetAuthorizedByDate?date=${dateStr}&legalEntityId=${legalEntityId}&bankCode=${bankCode}`,
        );
    }

    GetFilesRm(legalEntityId: string, skip: number, take: number): Observable<CustomResponseData<FilePaginatorRem>> {
        return this.http.get<CustomResponseData<FilePaginatorRem>>(
            `${this.API_URL}AutomaticDebit/GetFilesRm?legalEntityId=${legalEntityId}&skip=${skip}&take=${take}`,
        );
    }

    GetFilesRt(
        legalEntityId: string,
        beginDateStr: string,
        endDateStr: string,
    ): Observable<CustomResponseData<FilePaginatorRet>> {
        return this.http.get<CustomResponseData<FilePaginatorRet>>(
            `${this.API_URL}AutomaticDebit/GetFilesRt?legalEntityId=${legalEntityId}&beginDate=${beginDateStr}&endDate=${endDateStr}`,
        );
    }

    ResolvePendencyLineId(fileRetId: string, lineId: string): Observable<CustomResponse<any>> {
        let result = this.http.post<any>(`${this.API_URL}AutomaticDebit/ResolvePendencyLineId`, {
            Id: fileRetId,
            LineId: lineId,
        });
        return result;
    }

    accountingRetFile(fileRetId: string, username: string): Observable<CustomResponse<any>> {
        let result = this.http.post<any>(`${this.API_URL}AutomaticDebit/accountingRetFile`, {
            Id: fileRetId,
            Username: username,
        });
        return result;
    }
    RefreshStatusFile(fileRetId: string): Observable<CustomResponse<any>> {
        let result = this.http.post<any>(`${this.API_URL}AutomaticDebit/RefreshStatusFile`, {
            Id: fileRetId,
            FileType: 2,
        });
        return result;
    }

    Receiving(fileRetId: string, username: string, lineId: string): Observable<CustomResponse<any>> {
        let result = this.http.post<any>(`${this.API_URL}AutomaticDebit/Receiving`, {
            Id: fileRetId,
            LineId: lineId,
            Username: username,
            FileType: 2,
        });
        return result;
    }

    DownloadFile(id: string, fileType: number): Observable<DownloadedFile> {
        return this.http
            .post(
                `${this.API_URL}AutomaticDebit/DownloadFile`,
                { Id: id, FileType: fileType },
                {
                    responseType: 'arraybuffer',
                    observe: 'response',
                },
            )
            .pipe(
                map((httpResponse: HttpResponse<ArrayBuffer>) => this.extractFileNameAndBlobService(httpResponse)),
                catchError((error: HttpErrorResponse) => this.handleError(error)),
            );
    }

    CreateFile(fileData: DirectDebitContractFile): Observable<DownloadedFile> {
        return this.http
            .post(`${this.API_URL}AutomaticDebit/CreateFile`, fileData, {
                responseType: 'arraybuffer',
                observe: 'response',
            })
            .pipe(
                map((httpResponse: HttpResponse<ArrayBuffer>) => this.extractFileNameAndBlobService(httpResponse)),
                catchError((error: HttpErrorResponse) => this.handleError(error)), // Captura erro corretamente
            );
    }

    GetAccountingMonth(legalEntityId: string): Observable<any>{
        return this.http.get(`${this.API_URL}AutomaticDebit/GetAccountingMonth?legalEntityId=${legalEntityId}`);
    }

    importFile(formData: FormData): Observable<AutomaticDebitRetFile> {
        return this.http
            .post<AutomaticDebitRetFile>(`${this.API_URL}AutomaticDebit/ImportFile`, formData)
            .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
    }

    private handleError(error: HttpErrorResponse): Observable<never> {
        if (error?.error?.Message) {
            return new Observable((observer) => {
                observer.error(error?.error?.Message);
            });
        } else if (error.error instanceof ArrayBuffer) {
            return new Observable((observer) => {
                const textDecoder = new TextDecoder('utf-8');
                const errorText = textDecoder.decode(error.error);

                try {
                    const errorJson = JSON.parse(errorText);
                    observer.error(errorJson.message || errorJson.error || errorText);
                } catch {
                    observer.error(errorText);
                }
            });
        }
        return throwError(() => new Error(error.message || 'Erro desconhecido.'));
    }

    extractFileNameAndBlobService(httpResponse: HttpResponse<ArrayBuffer>): DownloadedFile {
        const headers = httpResponse.headers;
        const contentDisposition = headers.get('Content-Disposition');

        if (contentDisposition == null) {
            throw new Error(`Server Error: Cannot retrieve file name.
        Either Content-Dispostion header is not available or it is not exposed using Access-Control-Expose Header.`);
        }

        const fileNameItem = contentDisposition
            .split(';')
            .map((item) => item.trim())
            .find((item) => item.toLowerCase().startsWith('filename='));
        if (fileNameItem == null) {
            throw new Error(`Server didn't send a filename`);
        }

        let fileName = fileNameItem.split('=')[1];
        if (fileName.startsWith('"')) fileName = fileName.substring(1, fileName.length - 1);

        const contentType = headers.get('Content-Type');
        const blob = new Blob([httpResponse.body], { type: contentType });
        return {
            blob: blob,
            fileName: fileName,
        };
    }
}
